import { useCallback, useMemo, useRef, useState } from "react"

// --- Hooks ---
import { useMenuNavigation } from "@/tiptap/hooks/use-menu-navigation"
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Icons ---
import { ChevronDownIcon } from "@/tiptap/icons/chevron-down-icon"

import {
  useColorTextPopover,
  useRecentColors,
  getColorByValue,
} from "@/tiptap/ui/color-text-popover"
import {
  TEXT_COLORS,
  ColorTextButton,
} from "@/tiptap/ui/color-text-button"
import {
  HIGHLIGHT_COLORS,
  ColorHighlightButton,
} from "@/tiptap/ui/color-highlight-button"

import { Button } from "@/tiptap/ui-primitive/button"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/tiptap/ui-primitive/popover"
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"

// --- Utils ---
import { chunkArray } from "@/tiptap/lib/tiptap-advanced-utils"

// --- Styles ---
import { ButtonGroup } from "@/tiptap/ui-primitive/button-group";

export function RecentColorButton({
  colorObj,
  withLabel = false,
  onColorChanged,
  editor,
  ...props
}) {
  const colorSet = colorObj.type === "text" ? TEXT_COLORS : HIGHLIGHT_COLORS
  const color = getColorByValue(colorObj.value, colorSet)

  const commonProps = {
    tooltip: color.label,
    text: withLabel ? color.label : undefined,
    onApplied: () =>
      onColorChanged?.({
        type: colorObj.type,
        label: color.label,
        value: color.value,
      }),
    ...props,
  }

  return colorObj.type === "text" ? (
    <ColorTextButton
      textColor={color.value}
      label={color.label}
      editor={editor}
      {...commonProps} />
  ) : (
    <ColorHighlightButton
      highlightColor={color.value}
      label={color.label}
      editor={editor}
      {...commonProps} />
  );
}

export function ColorGroup({
  type,
  colors,
  onColorSelected,
  selectedIndex,
  startIndexOffset,
  editor
}) {
  return colors.map((group, groupIndex) => (
    <ButtonGroup key={`${type}-group-${groupIndex}`}>
      {group.map((color, colorIndex) => {
        const itemIndex =
          startIndexOffset +
          colors.slice(0, groupIndex).reduce((acc, g) => acc + g.length, 0) +
          colorIndex

        const isHighlighted = selectedIndex === itemIndex

        const commonProps = {
          tooltip: color.label,
          onApplied: () =>
            onColorSelected({ type, label: color.label, value: color.value }),
          tabIndex: isHighlighted ? 0 : -1,
          "data-highlighted": isHighlighted,
          "aria-label": `${color.label} ${type === "text" ? "text" : "highlight"} color`,
        }

        return type === "text" ? (
          <ButtonGroup key={`${type}-${color.value}-${colorIndex}`}>
            <ColorTextButton
              textColor={color.value}
              label={color.label}
              editor={editor}
              {...commonProps} />
          </ButtonGroup>
        ) : (
          <ButtonGroup key={`${type}-${color.value}-${colorIndex}`}>
            <ColorHighlightButton
              highlightColor={color.value}
              label={color.label}
              editor={editor}
              {...commonProps} />
          </ButtonGroup>
        );
      })}
    </ButtonGroup>
  ));
}

function RecentColorsSection({
  recentColors,
  onColorSelected,
  selectedIndex,
  editor
}) {
  if (recentColors.length === 0) return null

  return (
    <CardItemGroup>
      <CardGroupLabel>Recently used</CardGroupLabel>
      <ButtonGroup>
        {recentColors.map((colorObj, index) => (
          <RecentColorButton
            key={`recent-${colorObj.type}-${colorObj.value}`}
            colorObj={colorObj}
            onColorChanged={onColorSelected}
            tabIndex={selectedIndex === index ? 0 : -1}
            data-highlighted={selectedIndex === index}
            editor={editor} />
        ))}
      </ButtonGroup>
    </CardItemGroup>
  );
}

export function TextStyleColorPanel({
  maxColorsPerGroup = 5,
  maxRecentColors = 3,
  onColorChanged,
  editor
}) {
  const { recentColors, addRecentColor, isInitialized } =
    useRecentColors(maxRecentColors)
  const containerRef = useRef(null)

  const textColorGroups = useMemo(() => chunkArray(TEXT_COLORS, maxColorsPerGroup), [maxColorsPerGroup])

  const highlightColorGroups = useMemo(() => chunkArray(HIGHLIGHT_COLORS, maxColorsPerGroup), [maxColorsPerGroup])

  const allTextColors = useMemo(() => textColorGroups.flat(), [textColorGroups])
  const allHighlightColors = useMemo(() => highlightColorGroups.flat(), [highlightColorGroups])

  const textColorStartIndex = useMemo(
    () => (isInitialized ? recentColors.length : 0),
    [isInitialized, recentColors.length]
  )

  const highlightColorStartIndex = useMemo(
    () => textColorStartIndex + allTextColors.length,
    [textColorStartIndex, allTextColors.length]
  )

  const menuItems = useMemo(() => {
    const items = []

    if (isInitialized && recentColors.length > 0) {
      items.push(...recentColors.map((color) => ({
        type: color.type,
        value: color.value,
        label: `Recent ${color.type === "text" ? "text" : "highlight"} color`,
        group: "recent",
      })))
    }

    items.push(...allTextColors.map((color) => ({
      type: "text",
      value: color.value,
      label: color.label,
      group: "text",
    })))

    items.push(...allHighlightColors.map((color) => ({
      type: "highlight",
      value: color.value,
      label: color.label,
      group: "highlight",
    })))

    return items
  }, [isInitialized, recentColors, allTextColors, allHighlightColors])

  const handleColorSelected = useCallback(({
    type,
    label,
    value
  }) => {
    if (!containerRef.current) return false

    const highlighted = containerRef.current.querySelector('[data-highlighted="true"]')

    highlighted?.click()

    addRecentColor({ type, label, value })
    onColorChanged?.({ type, label, value })
  }, [addRecentColor, onColorChanged])

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    onSelect: (item) => {
      if (item)
        handleColorSelected({
          type: item.type,
          label: item.label,
          value: item.value,
        })
    },
    orientation: "both",
    autoSelectFirstItem: false,
  })

  return (
    <Card ref={containerRef} tabIndex={0} role="menu">
      <CardBody>
        {isInitialized && (
          <RecentColorsSection
            recentColors={recentColors}
            onColorSelected={handleColorSelected}
            selectedIndex={selectedIndex}
            editor={editor} />
        )}

        <CardItemGroup>
          <CardGroupLabel>Text color</CardGroupLabel>
          <ColorGroup
            type="text"
            colors={textColorGroups}
            onColorSelected={handleColorSelected}
            selectedIndex={selectedIndex}
            startIndexOffset={textColorStartIndex}
            editor={editor} />
        </CardItemGroup>

        <CardItemGroup>
          <CardGroupLabel>Highlight color</CardGroupLabel>
          <ColorGroup
            type="highlight"
            colors={highlightColorGroups}
            onColorSelected={handleColorSelected}
            selectedIndex={selectedIndex}
            startIndexOffset={highlightColorStartIndex}
            editor={editor} />
        </CardItemGroup>
      </CardBody>
    </Card>
  );
}

/**
 * Color text popover component for Tiptap editors.
 *
 * For custom popover implementations, use the `useColorTextPopover` hook instead.
 */
export function ColorTextPopover({
  editor: providedEditor,
  hideWhenUnavailable = false,
  onColorChanged,
  onClick,
  children,
  ref,
  ...buttonProps
}) {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = useState(false)
  const {
    isVisible,
    canToggle,
    activeTextStyle,
    activeHighlight,
    handleColorChanged,
    label,
    Icon,
  } = useColorTextPopover({ editor, hideWhenUnavailable, onColorChanged })

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    setIsOpen((prev) => !prev)
  }, [onClick])

  if (!isVisible) return null

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          data-appearance="default"
          role="button"
          aria-label={label}
          tooltip={label}
          disabled={!canToggle}
          data-disabled={!canToggle}
          onClick={handleClick}
          ref={ref}
          {...buttonProps}>
          {children ?? (
            <>
              <span
                className="tiptap-button-color-text-popover"
                style={
                  activeHighlight.color
                    ? ({
                    "--active-highlight-color": activeHighlight.color
                  })
                    : {}
                }>
                <Icon
                  className="tiptap-button-icon"
                  style={{ color: activeTextStyle.color || undefined }} />
              </span>
              <ChevronDownIcon className="tiptap-button-dropdown-small" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Text color options" side="bottom" align="start">
        <TextStyleColorPanel onColorChanged={handleColorChanged} editor={editor} />
      </PopoverContent>
    </Popover>
  );
}

export default ColorTextPopover
